<template>
  <div
    v-if="showTabsAsSelect"
    class="z-80 flex w-full cursor-default gap-2 opacity-100 print:hidden"
  >
    <DashboardTabsAsSelect
      v-model:selected-dashboard="selectedValue"
      :dashboard-list="dashboardList"
    />
    <button
      v-if="$can('create_dashboards') || $can('subscribe_dashboards')"
      class="aspect-square w-8 rounded-md border"
      @click="$emit('new-dashboard')"
    >
      <FontAwesomeIcon :icon="['fal', 'plus']" size="sm" />
    </button>
    <button
      v-if="$route.name === 'dashboard_page_route' && canOrganizeDashboards"
      class="aspect-square w-8 rounded-md border"
      @click="$emit('manage-dashboards')"
    >
      <FontAwesomeIcon :icon="['fal', 'star']" size="sm" />
    </button>
  </div>
  <div
    id="dashboard-tabs-container"
    class="flex w-full flex-row gap-4 border-b print:hidden"
    :class="[
      showTabsAsSelect
        ? 'z-100 h-1 -translate-y-64 overflow-hidden'
        : '-mt-2 mb-0.5'
    ]"
  >
    <div class="flex shrink-0 flex-row">
      <div class="flex flex-row place-content-between">
        <div class="flex flex-1 flex-row gap-4">
          <template v-if="showPredefinedSection">
            <span
              class="shrink-0 self-center px-1 text-xs font-semibold tracking-wide text-gray-400 uppercase"
            >
              {{ $t('global.predefined_views') }}
            </span>
            <DashboardTabItem
              v-for="dashboard in predefinedDashboards"
              :key="dashboard.id"
              :selected="selectedDashboard?.id === dashboard.id"
              :dashboard="dashboard"
              @action="
                (action_type) =>
                  $emit('action', {
                    type: action_type,
                    dashboardId: dashboard.id
                  })
              "
            />
            <template v-if="customDashboards.length > 0">
              <div
                class="mx-1 h-5 w-px self-center bg-gray-300 dark:bg-slate-500"
              />
              <span
                class="shrink-0 self-center px-1 text-xs font-semibold tracking-wide text-gray-400 uppercase"
              >
                {{ $t('global.my_views') }}
              </span>
              <DashboardTabItem
                v-for="dashboard in customDashboards"
                :key="dashboard.id"
                :selected="selectedDashboard?.id === dashboard.id"
                :dashboard="dashboard"
                @action="
                  (action_type) =>
                    $emit('action', {
                      type: action_type,
                      dashboardId: dashboard.id
                    })
                "
              />
            </template>
          </template>
          <template v-else>
            <DashboardTabItem
              v-for="dashboard in dashboardList"
              :key="dashboard.id"
              :selected="selectedDashboard?.id === dashboard.id"
              :dashboard="dashboard"
              @action="
                (action_type) =>
                  $emit('action', {
                    type: action_type,
                    dashboardId: dashboard.id
                  })
              "
            />
          </template>
        </div>
      </div>
    </div>
    <div
      id="tab-plus-container"
      class="flex flex-1 flex-row items-center gap-1"
    >
      <button
        v-if="$can('create_dashboards') || $can('subscribe_dashboards')"
        class="grid place-items-center rounded-md border p-1.5"
        @click="$emit('new-dashboard')"
      >
        <FontAwesomeIcon :icon="['fal', 'plus']" size="sm" />
      </button>
      <button
        v-if="canOrganizeDashboards"
        class="grid place-items-center rounded-md border p-1.5"
        @click="$emit('manage-dashboards')"
      >
        <FontAwesomeIcon :icon="['fal', 'star']" size="sm" />
      </button>
    </div>
  </div>
</template>
<script>
import DashboardTabItem from './DashboardTabItem'
import { computed } from 'vue'
import DashboardTabsAsSelect from '@c/Dashboard/Components/DashboardTabsAsSelect.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { canOrganizeDashboards } from '@u/helpers/dashboardPermissions'

export default {
  name: 'DashboardTabs',
  components: { FontAwesomeIcon, DashboardTabsAsSelect, DashboardTabItem },
  props: {
    dashboardList: {
      type: Array,
      required: true
    },
    selectedDashboard: {
      type: Object,
      required: false,
      default: null
    },
    showPredefinedSection: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: [
    'update:selectedDashboard',
    'action',
    'selected',
    'new-dashboard',
    'manage-dashboards'
  ],
  setup(props, { emit }) {
    const selectedValue = computed({
      get: () => props.selectedDashboard,
      set: (value) => emit('update:selectedDashboard', value)
    })
    return { selectedValue }
  },
  data() {
    return {
      intersectionObserver: null,
      showTabsAsSelect: true
    }
  },
  computed: {
    canOrganizeDashboards() {
      return canOrganizeDashboards((permission) => this.$can(permission))
    },
    predefinedDashboards() {
      return this.dashboardList.filter((dashboard) => dashboard.is_default)
    },
    customDashboards() {
      return this.dashboardList.filter((dashboard) => !dashboard.is_default)
    }
  },
  mounted() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.showTabsAsSelect = !entries[0].isIntersecting
      },
      {
        root: document.getElementById('dashboard-tabs-container'),
        threshold: 1
      }
    )
    this.intersectionObserver.observe(
      document.getElementById('tab-plus-container')
    )
  },
  beforeUnmount() {
    this.intersectionObserver.disconnect()
  },
  methods: {
    handleSaveTemplateClick() {},
    handleNewTemplateClick() {}
  }
}
</script>
